import React, { useState } from 'react';

/**
 * Normaliza lenguaje hablado cotidiano removiendo acentos, puntuación y mayúsculas.
 * Ej: "¿Cómo dar direcciones en inglés?" -> "como dar direcciones en ingles"
 */
function normalizeSpokenText(text) {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Convierte texto estructurado con markdown (**negrita**, *cursiva*, • viñetas, saltos de línea)
 * en elementos HTML limpios sin mostrar los asteriscos crudos.
 */
function renderFormattedMessage(text) {
  if (!text) return null;

  const lines = text.split('\n');

  return lines.map((line, lineIdx) => {
    if (!line.trim()) {
      return <div key={lineIdx} className="h-2" />;
    }

    const boldRegex = /\*\*(.*?)\*\*/g;
    let match;
    let lastIndex = 0;
    const elements = [];

    while ((match = boldRegex.exec(line)) !== null) {
      if (match.index > lastIndex) {
        elements.push(line.substring(lastIndex, match.index));
      }
      elements.push(
        <strong key={`bold-${match.index}`} className="font-extrabold text-cyan-300">
          {match[1]}
        </strong>
      );
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < line.length) {
      elements.push(line.substring(lastIndex));
    }

    const isBullet = line.trim().startsWith('•') || line.trim().startsWith('-') || line.trim().startsWith('1️⃣') || line.trim().startsWith('2️⃣') || line.trim().startsWith('💡');

    return (
      <div key={lineIdx} className={isBullet ? "pl-2 my-0.5" : "my-1"}>
        {elements.length > 0 ? elements : line}
      </div>
    );
  });
}

/**
 * Motor Semántico Local de Lenguaje Hablado Cotidiano para el Profesor Didier AI
 */
function generateSpokenLanguageAnswer(rawUserText) {
  const norm = normalizeSpokenText(rawUserText);

  // 1. DIRECCIONES Y UBICACIÓN (Direcciones, indicar caminos, llegar a lugares)
  if (/direccion|direcciones|ubicacion|llegar|llegar a|donde queda|calle|esquina|mapa|cuadra|izquierda|derecha|recto|straight|left|right|next to|near|metro|estacion/.test(norm)) {
    return `🗺️ **¿Cómo dar Direcciones e Indicaciones en Inglés?**

Para guiar a alguien o pedir ubicaciones en la ciudad usamos imperativos y preposiciones de lugar:

1️⃣ **Expresiones Clave para Indicar el Camino:**
• **Go straight ahead** ➔ *Sigue derecho.*
• **Turn left** ➔ *Gira a la izquierda.*
• **Turn right** ➔ *Gira a la derecha.*
• **Cross the street** ➔ *Cruza la calle.*
• **Go past the bank** ➔ *Pasa por el lado del banco.*

2️⃣ **Preposiciones para Decir Dónde Queda un Lugar:**
• **Next to** ➔ *Al lado de* (Ej: *"The metro station is next to the park"*).
• **Between** ➔ *Entre dos lugares* (Ej: *"The bakery is between the school and the pharmacy"*).
• **In front of** ➔ *En frente de*.
• **Behind** ➔ *Detrás de*.

💡 **¿Cómo Preguntar en Lenguaje Hablado?**
• *"Excuse me, how do I get to IE EOH?"* (Disculpe, ¿cómo llego a la IE Enrique Olaya Herrera?)
• *"Where is the nearest store?"* (¿Dónde queda la tienda más cercana?)`;
  }

  // 2. PASADO SIMPLE Y REGLAS (-IED, -ED, VERBOS IRREGULARES)
  if (/pasado|past|ied|ed|ayer|yesterday|pasada|pasado simple|regular|irregular|hice|estudie|jugue|fue|fui/.test(norm)) {
    return `📚 **El Pasado Simple en Inglés (Past Simple)**

Lo usamos para hablar de acciones completadas en el pasado.

1️⃣ **Regla de la -Y por -IED (Verbos Regulares):**
• **Consonante + Y** (como *study*, *try*, *cry*) ➔ Cambias la 'y' por **'ied'**:
  *Study* ➔ **Studied** | *Try* ➔ **Tried** | *Cry* ➔ **Cried**
• **Vocal + Y** (como *play*, *enjoy*, *stay*) ➔ Solo agregas **'ed'**:
  *Play* ➔ **Played** | *Enjoy* ➔ **Enjoyed**

2️⃣ **Verbos Irregulares Frecuentes:**
• *Go* ➔ **Went** (Fue / Iba)
• *Have* ➔ **Had** (Tenía / Tuvo)
• *Eat* ➔ **Ate** (Comió)
• *See* ➔ **Saw** (Vio)
• *Do* ➔ **Did** (Hizo)

💡 **Ejemplo en Oración:**
• *"Yesterday I studied English and played soccer with my classmates at IE EOH."* (Ayer estudié inglés y jugué fútbol con mis compañeros).`;
  }

  // 3. PRESENTE SIMPLE Y RUTINAS DIARIAS
  if (/presente|present|rutina|rutinas|diario|diariamente|todos los dias|manana|tarde|noche|levantar|desayunar|almorzar|tercera persona/.test(norm)) {
    return `🎒 **El Presente Simple y Rutinas Diarias (Daily Routine)**

Lo usamos para hablar de hábitos, costumbres y actividades que hacemos todos los días.

1️⃣ **Estructura General (I, You, We, They):**
• Sujeto + Verbo Base + Complemento
  *Ejemplo:* *"I wake up at 6:00 AM and go to school."* (Me despierto a las 6:00 AM y voy al colegio).

2️⃣ **Tercera Persona (He, She, It):**
• Al verbo se le agrega **'s'** o **'es'**:
  *Ejemplo:* *"She plays basketball in the afternoon."* (Ella juega baloncesto en la tarde).

💡 **Vocabulario de Rutina Diaria:**
• **Wake up** ➔ *Despertarse* | **Get up** ➔ *Levantarse*
• **Have breakfast** ➔ *Desayunar* | **Brush teeth** ➔ *Cepillarse*
• **Go to school** ➔ *Ir al colegio* | **Do homework** ➔ *Hacer la tarea*`;
  }

  // 4. VERBO TO BE Y PASADO (WAS / WERE)
  if (/to be|verbo be|soy|estoy|era|estaba|fueron|fui|was|were|am|is|are/.test(norm)) {
    return `📚 **El Verbo 'To Be' (Ser o Estar)**

Expresa identidad, origen, emociones o ubicación.

1️⃣ **En Presente (Am / Is / Are):**
• **I am** (Yo soy / estoy) ➔ *"I am an EOH student."*
• **He / She / It is** (Él/Ella/Eso es o está) ➔ *"She is happy."*
• **You / We / They are** (Tú/Nosotros/Ellos son o están) ➔ *"We are in class."*

2️⃣ **En Pasado (Was / Were):**
• **I / He / She / It ➔ WAS** (Era / Estaba) ➔ *"I was at home yesterday."*
• **You / We / They ➔ WERE** (Eran / Estaban) ➔ *"They were in Medellín."*

💡 **Negación:** Agregas **not** (*I am not*, *She is not*, *We were not*).`;
  }

  // 5. HABILIDADES Y VERBOS MODALES (CAN, CAN'T, MUST, SHOULD)
  if (/puedo|no puedo|poder|saber hacer|habilidad|can|cant|cannot|deber|deberia|must|should/.test(norm)) {
    return `🎯 **Habilidades y Verbos Modales en Inglés**

1️⃣ **CAN (Poder / Saber hacer):**
• Para habilidades y capacidades:
  *Ejemplo:* *"I can speak English and play soccer."* (Sé hablar inglés y jugar fútbol).

2️⃣ **CAN'T (No poder / No saber hacer):**
• Para incapacidades o prohibiciones:
  *Ejemplo:* *"He can't drive a car."* (Él no sabe manejar un carro).

3️⃣ **SHOULD (Debería - Consejo):**
• *Ejemplo:* *"You should study for the test."* (Deberías estudiar para el examen).

4️⃣ **MUST (Deber - Obligación):**
• *Ejemplo:* *"We must respect our teachers."* (Debemos respetar a nuestros profesores).`;
  }

  // 6. FAMILIA Y ADJETIVOS POSESIVOS (MY, YOUR, HIS, HER)
  if (/familia|mama|papa|hermano|hermana|abuela|abuelo|mi|tu|su|nuestro|my|your|his|her|our|their/.test(norm)) {
    return `👨‍👩‍👧‍👦 **Vocabulario de la Familia y Posesivos**

1️⃣ **Miembros de la Familia:**
• **Mother / Mom** ➔ *Madre / Mamá* | **Father / Dad** ➔ *Padre / Papá*
• **Brother** ➔ *Hermano* | **Sister** ➔ *Hermana*
• **Grandmother** ➔ *Abuela* | **Grandfather** ➔ *Abuelo*

2️⃣ **Adjetivos Posesivos (De quién es algo):**
• **My** ➔ *Mi / Mis* (*My brother*)
• **Your** ➔ *Tu / Tus* (*Your book*)
• **His** ➔ *Su (de él)* (*His father*)
• **Her** ➔ *Su (de ella)* (*Her sister*)
• **Our** ➔ *Nuestro / Nuestra* (*Our school IE EOH*)
• **Their** ➔ *Su (de ellos)* (*Their house*)`;
  }

  // 7. COLEGIO, MATERIAS Y GUSTOS (LIKE, PREFER)
  if (/colegio|escuela|clase|materia|materias|me gusta|no me gusta|preferir|like|dislike|subject|subjects/.test(norm)) {
    return `🏫 **Materias del Colegio y Expresar Gustos**

1️⃣ **Materias Escolares (School Subjects):**
• **Mathematics (Math)** ➔ *Matemáticas*
• **English** ➔ *Inglés* | **Science** ➔ *Ciencias*
• **History** ➔ *Historia* | **Arts** ➔ *Artes*
• **Physical Education (P.E.)** ➔ *Educación Física*

2️⃣ **Expresar lo que te Gusta:**
• **I like...** ➔ *Me gusta...* (*"I like English class"*).
• **I don't like...** ➔ *No me gusta...* (*"I don't like homework on weekends"*).
• **I prefer...** ➔ *Prefiero...* (*"I prefer interactive workshops"*).`;
  }

  // 8. SALUDOS, DESPEDIDAS Y PRESENTACIÓN PERSONAL
  if (/hola|saludo|saludos|despedida|despedidas|buenas|buenos dias|como te llamas|de donde eres|name|greeting/.test(norm)) {
    return `👋 **Saludos, Despedidas y Presentación Personal**

1️⃣ **Saludos (Greetings):**
• **Hello / Hi!** ➔ *¡Hola!*
• **Good morning** ➔ *Buenos días.*
• **Good afternoon** ➔ *Buenas tardes.*
• **Good evening** ➔ *Buenas noches (al llegar).*

2️⃣ **Presentación Personal:**
• *"What is your name?"* ➔ *"My name is..."* (Mi nombre es...)
• *"Where are you from?"* ➔ *"I am from Colombia."* (Soy de Colombia).
• *"How old are you?"* ➔ *"I am 13 years old."* (Tengo 13 años).

3️⃣ **Despedidas (Farewells):**
• **Goodbye / Bye!** ➔ *Hasta luego.*
• **See you tomorrow!** ➔ *¡Nos vemos mañana!*`;
  }

  // 9. MISIONES, EJERCICIOS Y PUNTOS XP
  if (/mision|misiones|taller|ejercicio|examen|icfes|puntos|xp|nota|notas|como hago|como entro/.test(norm)) {
    return `🎮 **¿Cómo realizar tus Misiones y ganar Puntos XP?**

1️⃣ **Inicia Sesión:** Da clic en **"🎓 Acceso Estudiantes"** e ingresa con tu Código Único EOH.
2️⃣ **Selecciona tu Grado:** Elige Grado 6°, 7° o 9° desde la pantalla principal.
3️⃣ **Elige la Unidad:** Selecciona la Unidad de estudio (U1 a U5).
4️⃣ **Resuelve los Ejercicios:**
   • **Bloque 1:** Taller Combinatorio (20 Preguntas)
   • **Bloque 2:** Práctica ICFES (10 Preguntas)
5️⃣ **Gana Puntos XP:** Cada respuesta correcta te otorga +10 XP y registra tu calificación automáticamente en el Panel Docente del Profesor Didier.

💡 **Consejo:** ¡Haz clic en cualquier palabra en inglés durante los ejercicios para ver su traducción en español y escuchar la pronunciación!`;
  }

  // 10. RESPUESTA PEDAGÓGICA GENERAL PARA CUALQUIER OTRA CONSULTA EN LENGUAJE HABLADO
  return `💡 **Explicación del Profesor Didier AI:**

Sobre tu duda respecto a **"${rawUserText}"**:

En la asignatura de Inglés de la IE Enrique Olaya Herrera nos enfocamos en el aprendizaje activo y la comunicación en contexto real (**Task-Based Learning**).

1️⃣ **Estructura Gramatical:**
   Identifica si tu oración necesita un sujeto (*I, You, He, She, We, They*) y el verbo principal en la acción.

2. **Ejemplo en Inglés:**
   • *"Learning English opens new opportunities for our future at IE EOH."*
   *(Aprender inglés abre nuevas oportunidades para nuestro futuro en la IE EOH).*

3. **Consejo del Profe:**
   Puedes explorar el **Vocabulario Hub**, el **Diccionario Olayista** en el menú superior o consultar cualquier duda sobre las misiones aquí conmigo en cualquier momento.`;
}

export default function StudentTutorChat() {
  const [messages, setMessages] = useState([
    { 
      sender: 'tutor', 
      text: '¡Hola! ✈️ Soy el Profesor Didier AI, tu tutor pedagógico de inglés en la IE Enrique Olaya Herrera.\n\nPuedes preguntarme en tu lenguaje cotidiano sobre cualquier tema: cómo dar direcciones, pasado simple, presente, verbo to be, cómo hacer las misiones o dudas de vocabulario. ¡Estoy para ayudarte!' 
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const userText = inputMessage;
    const newMessages = [...messages, { sender: 'user', text: userText }];
    setMessages(newMessages);
    setInputMessage('');
    setIsTyping(true);

    const apiKey = process.env.REACT_APP_GEMINI_API_KEY || process.env.GEMINI_API_KEY;
    let tutorReply = "";

    if (apiKey && apiKey !== "AIzaSy_DEFAULT_KEY") {
      try {
        const response = await fetch(
          `https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent?key=${apiKey}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              contents: [
                {
                  parts: [
                    {
                      text: `Eres el Profesor Didier Alvarez Ramos, docente de Inglés pedagógico para estudiantes de secundaria de la Institución Educativa Enrique Olaya Herrera en Medellín, Colombia. Tu estilo es motivador, cercano, pedagógico y usas la metodología Task-Based Learning (TBL). El estudiante te está preguntando en lenguaje cotidiano o hablado en español. Responde en español de forma cercana explicando en detalle lo que necesita, dando reglas gramaticales, ejemplos en inglés con su traducción al español. Pregunta del estudiante: "${userText}"`
                    }
                  ]
                }
              ]
            })
          }
        );

        if (response.ok) {
          const data = await response.json();
          tutorReply = data.candidates?.[0]?.content?.parts?.[0]?.text;
        }
      } catch (err) {
        console.warn("[StudentTutorChat] API Gemini no disponible, usando motor pedagógico inteligente local:", err);
      }
    }

    // Si no hay respuesta de la API o la API falló, usar el Motor Semántico Local de Lenguaje Hablado
    if (!tutorReply) {
      tutorReply = generateSpokenLanguageAnswer(userText);
    }

    setMessages(prev => [...prev, { sender: 'tutor', text: tutorReply }]);
    setIsTyping(false);
  };

  return (
    <div className="bg-slate-800/90 border border-slate-700/80 rounded-2xl p-6 shadow-2xl space-y-6 backdrop-blur-sm text-slate-100 max-w-2xl mx-auto">
      
      {/* ENCABEZADO */}
      <div className="flex items-center gap-4 border-b border-slate-700 pb-4">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-cyan-600 to-blue-500 flex items-center justify-center text-2xl shadow-lg shrink-0">
          🤖
        </div>
        <div>
          <span className="text-[10px] font-bold uppercase tracking-widest text-cyan-300 bg-cyan-950/80 px-2.5 py-0.5 rounded-full border border-cyan-500/30">
            Tutor IA EOH en Vivo
          </span>
          <h2 className="text-lg font-black text-white mt-1">
            Pregúntale al Profesor Didier AI
          </h2>
          <p className="text-slate-400 text-xs">
            Comprende lenguaje cotidiano, explicaciones gramaticales y ayuda en tiempo real para tus misiones.
          </p>
        </div>
      </div>

      {/* VENTANA DE CONVERSACIÓN CON SCROLLBAR OSCURO INTEGRADO */}
      <div 
        className="bg-slate-950/90 border border-slate-700/60 rounded-2xl p-4 h-96 overflow-y-auto space-y-4 flex flex-col shadow-inner"
        style={{ scrollbarWidth: 'thin', scrollbarColor: '#334155 #0f172a' }}
      >
        {messages.map((msg, idx) => (
          <div 
            key={idx} 
            className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`max-w-[88%] p-4 rounded-2xl text-xs sm:text-sm leading-relaxed shadow-lg ${
              msg.sender === 'user' 
                ? 'bg-indigo-600 text-white rounded-br-none' 
                : 'bg-slate-900 border border-slate-700/80 text-slate-100 rounded-bl-none'
            }`}>
              {renderFormattedMessage(msg.text)}
            </div>
          </div>
        ))}

        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-slate-900 border border-slate-700 text-cyan-300 p-3.5 rounded-2xl rounded-bl-none text-xs italic animate-pulse flex items-center gap-2">
              <span>🤖</span> Profesor Didier AI analizando tu consulta...
            </div>
          </div>
        )}
      </div>

      {/* BOTONES DE SUGERENCIA RÁPIDA DE PREGUNTAS */}
      <div className="flex flex-wrap gap-1.5 pt-1">
        {[
          "¿Cómo dar direcciones en inglés?",
          "¿Cuándo se cambia la -y por -ied en pasado?",
          "¿Cómo uso el Presente Simple?",
          "Explícame el Verbo To Be",
          "Diferencia entre Can y Can't"
        ].map((suggestion, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => {
              setInputMessage(suggestion);
            }}
            className="text-[11px] bg-slate-900/80 hover:bg-cyan-900/60 hover:text-cyan-200 border border-slate-700/60 hover:border-cyan-500/40 text-slate-300 px-3 py-1 rounded-full transition cursor-pointer shadow-sm"
          >
            💡 {suggestion}
          </button>
        ))}
      </div>

      {/* CAMPO DE ENTRADA DE TEXTO */}
      <form onSubmit={handleSendMessage} className="flex gap-2">
        <input
          type="text"
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          placeholder="Escribe tu duda en lenguaje normal (ej. cómo doy direcciones, pasado simple)..."
          className="flex-1 bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-xs text-white outline-none focus:border-cyan-500 transition"
        />
        <button
          type="submit"
          disabled={isTyping}
          className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 disabled:opacity-50 text-white font-bold px-5 py-2.5 rounded-xl text-xs shadow-lg transition flex items-center gap-1 cursor-pointer"
        >
          Enviar 🚀
        </button>
      </form>
    </div>
  );
}